import isNull from 'lodash/isNull';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';
import querystring from 'querystring';
import { getDefaultData } from '../utils';
import { getJson, sendJson } from '../utils/fetch';
import { appendQueryString } from '../utils/urlUtils';

const { submitUrl, lang } = window.__DATA__;
const currentUrl = window.location.href.split('?')[0];
// Did we land directly on a /k/:privateKey URL (existing/invited participant) ?
const arrivedWithKey = !!window.__DATA__?.data?.privateKey;

const specialValues = {
  true: true,
  false: false,
  null: null,
  undefined: undefined,
};

export function parseUrlQuery() {
  return mapValues(querystring.parse(window.location.search.substring(1).toString()), (value) =>
    isString(value) && specialValues.hasOwnProperty(value) ? specialValues[value] : value,
  );
}

function isUndefinedOrNull(value) {
  return isNull(value) || isUndefined(value);
}

const params = omitBy({ lang, ...parseUrlQuery() }, isUndefinedOrNull);

const host = appendQueryString(submitUrl || currentUrl, params);

const apiHost = window.__DATA__.api.api ?? `${currentUrl}/api`;

class RegistrationV2Service {
  constructor() {
    // Known private key of the current participant: from the initial data (existing
    // user landing on /k/:key) or captured after creation on an open form.
    this._privateKey = window.__DATA__?.data?.privateKey;
    // Canonical /k/:privateKey form URL. For an existing user it's the current page
    // URL; for a user created on an open form it's provided by the server (setFormUrl).
    this._formUrl = arrivedWithKey ? currentUrl : undefined;
  }

  // Called after a save that returns the participant, so subsequent PATCH/refresh can
  // target the /k/:privateKey route (needed for users created on an open form).
  setPrivateKey(privateKey) {
    if (privateKey) this._privateKey = privateKey;
  }

  // Store the canonical form URL returned by the server (POST create / refresh) so we
  // target the /k/:privateKey route without rebuilding it from window.location.
  setFormUrl(formUrl) {
    if (formUrl) this._formUrl = formUrl.split('?')[0];
  }

  // Build a URL against the participant's /k/:privateKey form route.
  getUserFormUrl(extraParams = {}) {
    return appendQueryString(this._formUrl, { ...params, ...extraParams });
  }

  addResponse(body) {
    return sendJson('POST', host, body);
  }

  patchResponse(patch) {
    // Users landing on /k/:key patch to `host` (which may be a custom submitUrl).
    // Users created this session (open form) patch to the freshly known /k/:privateKey.
    const url = !arrivedWithKey && this._formUrl ? this.getUserFormUrl() : host;
    return sendJson('PATCH', url, patch);
  }

  upsertResponse(body) {
    const defaultData = getDefaultData();
    if (this._privateKey || defaultData?._id) {
      return this.patchResponse(body);
    }
    return this.addResponse(body);
  }

  getAvailableLocations(slot, appointmentSelectFieldId) {
    return sendJson('POST', apiHost + '/appointments/available-locations', { slot, appointmentSelectFieldId });
  }

  // Re-fetch the participant data + server-repopulated fields (JSON variant of the
  // GET /k/:privateKey form route). Used to refresh data on tab change. Resolves to
  // null when no form URL is known yet (e.g. creation failed).
  refreshParticipantForm() {
    if (!this._formUrl) return Promise.resolve(null);
    return getJson(this.getUserFormUrl({ format: 'json' }));
  }
}

export default new RegistrationV2Service();
